import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { Ban, Copy, MoreHorizontal, Pencil, Power, PowerOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { DataTable, type DataTableFeatures } from "~/components/data-table";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui";
import type { GroupRow } from "~/features/admin/groups/types";

interface GroupsTableProps {
  rows: GroupRow[];
  onSelect: (id: string) => void;
  onEdit: (group: GroupRow) => void;
  onActivate: (group: GroupRow) => void;
  onDeactivate: (group: GroupRow) => void;
  emptyMessage: string;
  globalFilter: string;
  onGlobalFilterChange: (next: string) => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
}

const helper = createColumnHelper<DataTableFeatures, GroupRow>();

export function GroupsTable({
  rows,
  onSelect,
  onEdit,
  onActivate,
  onDeactivate,
  emptyMessage,
  globalFilter,
  onGlobalFilterChange,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
}: GroupsTableProps) {
  const { t } = useTranslation();

  const handleCopyId = async (id: string) => {
    try {
      await copyToClipboard(id);
      toast.success(t("feature.common.idCopied"));
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  };

  const columns: ColumnDef<DataTableFeatures, GroupRow, any>[] = [
    helper.display({
      id: "deactivatedAt",
      header: () => "",
      enableSorting: false,
      enableGlobalFilter: false,
      cell: (info) => {
        if (!info.row.original.isDeactivated) return null;
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <Ban className="h-4 w-4 text-warning" aria-label={t("ui.deactivated")} />
            </TooltipTrigger>
            <TooltipContent>{t("ui.deactivated")}</TooltipContent>
          </Tooltip>
        );
      },
    }),
    helper.accessor("name", {
      id: "name",
      header: () => t("ui.name"),
      cell: (info) => <span className="font-medium text-fg-1">{info.getValue()}</span>,
    }),
    helper.accessor("description", {
      id: "description",
      header: () => t("ui.description"),
      cell: (info) => <span className="text-fg-2">{info.getValue()}</span>,
    }),
    helper.display({
      id: "actions",
      header: () => "",
      enableSorting: false,
      enableGlobalFilter: false,
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex items-center justify-end gap-1.5">
            <Button type="button" variant="outline" size="sm" onClick={() => onSelect(row.id)}>
              {t("ui.view")}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => onEdit(row)}>
              <Pencil />
              <span>{t("ui.edit")}</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="icon" aria-label={t("ui.operations")}>
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {row.isDeactivated ? (
                  <DropdownMenuItem onSelect={() => onActivate(row)}>
                    <Power />
                    <span>{t("ui.activate")}</span>
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onSelect={() => onDeactivate(row)}>
                    <PowerOff />
                    <span>{t("ui.deactivate")}</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={() => handleCopyId(row.id)}>
                  <Copy />
                  <span>{t("ui.copyId")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    }),
  ];

  return (
    <DataTable<GroupRow>
      columns={columns}
      data={rows}
      emptyMessage={emptyMessage}
      globalFilter={globalFilter}
      onGlobalFilterChange={onGlobalFilterChange}
      pageIndex={pageIndex}
      onPageIndexChange={onPageIndexChange}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
    />
  );
}
